/**
 * `/api/company-portal/*`, for a signed-in company contact. The server checks
 * access per feature and per employee on every call; the UI only hides what
 * it would refuse.
 */
import axiosInstance from "../../../api/axiosConfig";
import { handleAPIError } from "../../../api/utils/errors";
import { endPortalPreview, PREVIEW_HEADER, portalPreview } from "../preview";
import type {
  BudgetImpact,
  CompanyApproval,
  CompanyMessage,
  PortalDocument,
  PortalHome,
  PortalMe,
  PortalRequest,
  PortalSpend,
  RequestForm,
  RequestInput
} from "../types";

const call = async <T>(run: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const { data } = await run();
    return data;
  } catch (error) {
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

const BASE = "/company-portal";

axiosInstance.interceptors.request.use((config) => {
  const preview = portalPreview();
  if (preview && config.url?.startsWith(BASE)) {
    config.headers.set(PREVIEW_HEADER, preview);
  }
  return config;
});

export const PortalAPI = {
  me: () => call(() => axiosInstance.get<PortalMe>(`${BASE}/me`)),
  home: () => call(() => axiosInstance.get<PortalHome>(`${BASE}/home`)),
  employeesCsv: () =>
    call(() =>
      axiosInstance.get<string>(`${BASE}/employees.csv`, {
        responseType: "text"
      })
    ),
  documents: (individualId: string) =>
    call(() =>
      axiosInstance.get<{ documents: PortalDocument[] }>(
        `${BASE}/employees/${individualId}/documents`
      )
    ).then((d) => d.documents),
  documentUrl: (individualId: string, nodeId: string) =>
    call(() =>
      axiosInstance.get<{ url: string }>(
        `${BASE}/employees/${individualId}/documents/${nodeId}/url`
      )
    ).then((d) => d.url),
  messages: (individualId: string) =>
    call(() =>
      axiosInstance.get<{ messages: CompanyMessage[] }>(
        `${BASE}/employees/${individualId}/messages`
      )
    ).then((d) => d.messages),
  postMessage: (individualId: string, body: string) =>
    call(() =>
      axiosInstance.post<{ message: CompanyMessage }>(
        `${BASE}/employees/${individualId}/messages`,
        { body }
      )
    ).then((d) => d.message),
  requestForm: () =>
    call(() => axiosInstance.get<RequestForm>(`${BASE}/request-form`)),
  budgetImpact: (costCenter: string, caseType: string) =>
    call(() =>
      axiosInstance.get<{ impact: BudgetImpact | null }>(
        `${BASE}/budget-impact`,
        { params: { cost_center: costCenter, case_type: caseType } }
      )
    ).then((d) => d.impact),
  createRequest: (input: RequestInput) =>
    call(() =>
      axiosInstance.post<{ request: PortalRequest }>(`${BASE}/requests`, input)
    ).then((d) => d.request),
  approveRequest: (id: string) =>
    call(() =>
      axiosInstance.post<{ request: PortalRequest }>(
        `${BASE}/requests/${id}/approve`
      )
    ).then((d) => d.request),
  declineRequest: (id: string, reason: string) =>
    call(() =>
      axiosInstance.post<{ request: PortalRequest }>(
        `${BASE}/requests/${id}/decline`,
        { reason }
      )
    ).then((d) => d.request),
  replyToRequest: (id: string, text: string) =>
    call(() =>
      axiosInstance.post<{ request: PortalRequest }>(
        `${BASE}/requests/${id}/reply`,
        { text }
      )
    ).then((d) => d.request),
  answerApproval: (id: string, decision: "approve" | "decline", note: string) =>
    call(() =>
      axiosInstance.post<{ approval: CompanyApproval }>(
        `${BASE}/approvals/${id}/answer`,
        { decision, note }
      )
    ).then((d) => d.approval),
  approvalAttachment: (id: string) =>
    call(() =>
      axiosInstance.get<{
        name: string;
        url: string | null;
        text: string | null;
      }>(`${BASE}/approvals/${id}/attachment`)
    ),
  spend: () => call(() => axiosInstance.get<PortalSpend>(`${BASE}/spend`)),
  /** Public. Always answers the same, so it can't be used to find contacts. */
  sendSignInLink: (email: string) =>
    call(() =>
      axiosInstance.post<{ sent: boolean }>(`${BASE}/sign-in-link`, { email })
    )
};

/** Whether the signed-in Firebase user is a company contact. Never throws,
 * so a portal outage can't block firm or client sign-in. */
export const isCompanyContact = async (): Promise<boolean> => {
  endPortalPreview();
  try {
    await PortalAPI.me();
    return true;
  } catch {
    return false;
  }
};
